import React from 'react';
import { isColorDark, getButtonClasses } from '../../../config/colors';
import './style.css';

//const getHref = (link) => {
//  const item = Array.isArray(link) ? link[0] : link;
//  if (!item) return '#';
//  const url = item['@id'] || '#';
//  const isFile = item['@type'] === 'File';
//  return isFile ? `${url}/@@download/file` : url;
//};
const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  
  // Check if it's a PDF file
  const isPDF = item['file']?.['content-type'] === 'application/pdf' || 
                item['mime_type'] === 'application/pdf' ||
                url.toLowerCase().endsWith('.pdf');
  
  // Only use @@display-file for PDFs, keep @@download for other files
  return (isFile && isPDF) ? url.replace('/@@download/file', '/@@display-file/file') : url;
};

const extractTOCEntriesInSection = (blockId, blocks, layout, data = {}) => {
  const allowH2 = data.useH2 ?? true;
  const allowH3 = data.useH3 ?? true;
  const allowedTypes = [
    ...(allowH2 ? ['heading-two', 'h2'] : []),
    ...(allowH3 ? ['heading-three', 'h3'] : []),
  ];

  const startIndex = layout.indexOf(blockId);
  const nextSameBlock = layout
    .slice(startIndex + 1)
    .find((id) => blocks[id]?.['@type'] === 'buttonRow');
  const endIndex = nextSameBlock
    ? layout.indexOf(nextSameBlock)
    : layout.length;
  const targetIds = layout.slice(startIndex + 1, endIndex);

  const entries = [];

  targetIds.forEach((id) => {
    const block = blocks[id];
    if (
      (block?.['@type'] === 'text' || block?.['@type'] === 'slate') &&
      Array.isArray(block.value)
    ) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children
            ?.map((c) => c.text)
            .join('')
            .trim();
          const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          if (text) entries.push({ id: anchor, title: text });
        }
      });
    }
  });

  return entries;
};

const View = ({ data = {}, properties = {}, block }) => {
  const image = data.backgroundImage?.[0];
  const alignment = data.alignment || 'left';
  const buttonsPosition = data.buttonsPosition || 'below';
  const imageUrl = image?.['@id'] ? `${image['@id']}/@@images/image` : '';
  const displayAsList = data.displayAsList === true;

  const backgroundColor = data.backgroundColor || '#ffffff';
  const noBg = !imageUrl;
  const darkButtons = noBg && !isColorDark(backgroundColor);
  const colorLabel = backgroundColor
    .replace(/var\(--/, '')
    .replace(/\)/, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase();

  const wrapperStyle = displayAsList
    ? {}
    : {
        backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
        backgroundColor,
      };

  const customClass = data.customClass || '';
  const wrapperClass = displayAsList
    ? `button-row-list-mode${customClass ? ` ${customClass}` : ''}`
    : `button-row-block ${data.isFullWidth ? 'full-width' : ''} ${noBg ? 'no-bg' : ''} ${darkButtons ? 'dark-buttons' : ''} color-${colorLabel}${customClass ? ` ${customClass}` : ''}`;

  const layout = properties.blocks_layout?.items || [];
  const blocks = properties.blocks || {};
  const tocEntries = data.useTOC
    ? extractTOCEntriesInSection(block, blocks, layout, data)
    : [];

  const blockId = data.title
    ? data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    : undefined;

  const renderButtons = (buttons, isListMode = false) => {
    if (isListMode) {
      return (
        <div className="button-row-list">
          <p>
            {buttons.map((btn, i) => (
              <React.Fragment key={i}>
                {(() => {
                  const href = getHref(btn.link);
                  const isExternal = href?.startsWith('http');
                  return (
                    <a
                      href={href}
                      {...(isExternal
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {btn.label}
                    </a>
                  );
                })()}
                {i < buttons.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </p>
        </div>
      );
    }

    return (
      <div
        className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}
      >
        {buttons.map((btn, i) => {
          const href = getHref(btn.link);
          const isExternal = href?.startsWith('http');
          const { className: btnClass, style: btnStyle } = getButtonClasses(btn.buttonStyle || '', 'row-button');

          return (
            <a
              key={i}
              href={href}
              className={btnClass}
              style={btnStyle}
              {...(isExternal
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {btn.label}
            </a>
          );
        })}
      </div>
    );
  };

  const renderTOCButtons = (isListMode = false) => {
    if (isListMode) {
      return (
        <div className="button-row-list">
          <p>
            {tocEntries.map((entry, i) => (
              <React.Fragment key={i}>
                <a href={`#${entry.id}`}>{entry.title}</a>
                {i < tocEntries.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </p>
        </div>
      );
    }

    return (
      <div
        className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}
      >
        {tocEntries.map((entry, i) => (
          <a key={i} href={`#${entry.id}`} className="row-button">
            {entry.title}
          </a>
        ))}
      </div>
    );
  };

  const contentSection = (
    <div className="button-row-content">
      {data.preHeader && <div className="pre-header">{data.preHeader}</div>}
      {data.title && <h1>{data.title}</h1>}
      {data.text && <p>{data.text}</p>}
    </div>
  );

  const buttonsSection = data.useTOC
    ? renderTOCButtons(displayAsList)
    : data.buttons?.length > 0 && renderButtons(data.buttons, displayAsList);

  return (
    <div id={blockId} className={wrapperClass} style={wrapperStyle}>
      {!displayAsList && imageUrl && <div className="overlay" />}
      <div
        className={
          displayAsList
            ? ''
            : `button-row-inner align-${alignment} buttons-${buttonsPosition}`
        }
      >
        {buttonsPosition === 'beside' && !displayAsList ? (
          <div className="button-row-flex-container">
            {contentSection}
            {buttonsSection}
          </div>
        ) : (
          <>
            {contentSection}
            {buttonsSection}
          </>
        )}
      </div>
    </div>
  );
};

export default View;
